import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Printer,
  Trash2,
  Search,
  X,
  Package2,
  Layers,
} from "lucide-react";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  IconButton,
  InputAdornment,
  Checkbox,
  Tab,
  Tabs,
} from "@mui/material";

const ViewDesignRecipe = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [recipeId, setRecipeId] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    unit: "",
    cost: "",
    notes: "",
  });
  const [materialSets, setMaterialSets] = useState([
    {
      materialName: "",
      unit: "",
      avgPerPcs: "",
      rate: "",
      supplier: "",
    },
  ]);
  const [sectionData, setSectionData] = useState({
    bottom: [],
    dupatta: [],
    fabric: [],
    kurti: [],
    pent: [],
    top: [],
    otherCosts: [],
  });
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [materialSearchQuery, setMaterialSearchQuery] = useState("");
  const [tempSelectedMaterials, setTempSelectedMaterials] = useState([]);
  const [jobSets, setJobSets] = useState([
    {
      jobType: "",
      rate: "",
      processDays: "",
      karigarName: "",
    },
  ]);
  const [otherCosts, setOtherCosts] = useState([
    {
      costName: "",
      rate: "",
    },
  ]);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [recipeData, setRecipeData] = useState({});
  const [isViewMode, setIsViewMode] = useState(false);

  // Sample material data (replace with your actual data)
  const materialsList = [
    {
      id: 1,
      name: "Cotton Fabric",
      colors: "White, Black",
      unit: "meters",
      avgPrice: 150,
      image:
        "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
    {
      id: 2,
      name: "Silk Fabric",
      colors: "Red, Blue",
      unit: "meters",
      avgPrice: 300,
      image:
        "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    },
    // Add more materials as needed
  ];

  const filteredMaterials = materialsList.filter(material =>
    material.name.toLowerCase().includes(materialSearchQuery.toLowerCase()),
  );

  const jobTypes = [
    "Cutting",
    "Stitching",
    "Embroidery",
    "Printing",
    "Washing",
    "Packaging",
  ];
  const karigars = [
    "Karigar A",
    "Karigar B",
    "Karigar C",
    "Karigar D",
    "Karigar E",
  ];

  useEffect(() => {
    // Get recipe ID from URL params
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    setRecipeId(id);
  }, [location.search]);

  const handleBack = () => {
    navigate("/production/design-recipe");
  };

  const handleSectionClick = section => {
    setSelectedSection(section);
    setShowRecipeForm(true);

    // If section has existing data, pre-fill the form
    if (recipeData[section]) {
      setMaterialSets(recipeData[section].materials);
      setJobSets(recipeData[section].jobs);
      setOtherCosts(recipeData[section].otherCosts);
    } else {
      // Reset form for new data
      setMaterialSets([
        { materialName: "", unit: "", avgPerPcs: "", rate: "", supplier: "" },
      ]);
      setJobSets([{ jobType: "", rate: "", processDays: "", karigarName: "" }]);
      setOtherCosts([{ costName: "", rate: "" }]);
    }
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    console.log("Material sets:", materialSets);
    // Handle form submission here
  };

  const handleDelete = (sectionId, itemId) => {
    setSectionData(prev => ({
      ...prev,
      [sectionId]: prev[sectionId].filter(item => item.id !== itemId),
    }));
  };

  const handlePrint = sectionId => {
    console.log("Printing section:", sectionId);
    // Handle print functionality
  };

  const handleMaterialChange = (index, field, value) => {
    const updatedSets = [...materialSets];
    updatedSets[index][field] = value;
    setMaterialSets(updatedSets);
  };

  const addMaterialSet = () => {
    setMaterialSets([
      ...materialSets,
      {
        materialName: "",
        unit: "",
        avgPerPcs: "",
        rate: "",
        supplier: "",
      },
    ]);
  };

  const removeMaterialSet = index => {
    if (materialSets.length > 1) {
      const updatedSets = materialSets.filter((_, i) => i !== index);
      setMaterialSets(updatedSets);
    }
  };

  const handleMaterialSelect = material => {
    setTempSelectedMaterials(prev => {
      const isSelected = prev.some(m => m.id === material.id);
      if (isSelected) {
        return prev.filter(m => m.id !== material.id);
      } else {
        return [...prev, material];
      }
    });
  };

  const handleAddSelectedMaterials = () => {
    const newMaterialSets = tempSelectedMaterials.map(material => ({
      materialName: material.name,
      unit: material.unit,
      avgPerPcs: "",
      rate: material.avgPrice.toString(),
      supplier: "",
    }));
    setMaterialSets(newMaterialSets);
    setShowMaterialModal(false);
    setTempSelectedMaterials([]);
  };

  const handleOpenMaterialModal = () => {
    setTempSelectedMaterials(
      materialSets
        .map(set => {
          const material = materialsList.find(m => m.name === set.materialName);
          return material || null;
        })
        .filter(Boolean),
    );
    setShowMaterialModal(true);
  };

  const sections = [
    { id: "bottom", label: "Bottom" },
    { id: "dupatta", label: "Dupatta" },
    { id: "fabric", label: "Fabric" },
    { id: "kurti", label: "Kurti" },
    { id: "pent", label: "Pent" },
    { id: "top", label: "Top" },
    { id: "otherCosts", label: "Other Costs" },
  ];

  const units = ["meters", "pieces", "kg", "grams", "yards", "sets"];
  const materials = [
    "Cotton Fabric",
    "Silk Fabric",
    "Polyester",
    "Linen",
    "Denim",
    "Wool",
    "Synthetic",
  ];
  const suppliers = [
    "Supplier A",
    "Supplier B",
    "Supplier C",
    "Supplier D",
    "Local Market",
  ];

  const handleJobChange = (index, field, value) => {
    const updatedSets = [...jobSets];
    updatedSets[index][field] = value;
    setJobSets(updatedSets);
  };

  const addJobSet = () => {
    setJobSets([
      ...jobSets,
      {
        jobType: "",
        rate: "",
        processDays: "",
        karigarName: "",
      },
    ]);
  };

  const removeJobSet = index => {
    if (jobSets.length > 1) {
      const updatedSets = jobSets.filter((_, i) => i !== index);
      setJobSets(updatedSets);
    }
  };

  const handleOtherCostChange = (index, field, value) => {
    const updatedCosts = [...otherCosts];
    updatedCosts[index][field] = value;
    setOtherCosts(updatedCosts);
  };

  const addOtherCost = () => {
    setOtherCosts([
      ...otherCosts,
      {
        costName: "",
        rate: "",
      },
    ]);
  };

  const removeOtherCost = index => {
    if (otherCosts.length > 1) {
      const updatedCosts = otherCosts.filter((_, i) => i !== index);
      setOtherCosts(updatedCosts);
    }
  };

  const handleAddToRecipe = () => {
    // Add current data to recipe for the selected section
    setRecipeData(prev => ({
      ...prev,
      [selectedSection]: {
        materials: materialSets,
        jobs: jobSets,
        otherCosts: otherCosts,
      },
    }));

    // Reset form data
    setMaterialSets([
      { materialName: "", unit: "", avgPerPcs: "", rate: "", supplier: "" },
    ]);
    setJobSets([{ jobType: "", rate: "", processDays: "", karigarName: "" }]);
    setOtherCosts([{ costName: "", rate: "" }]);

    // Hide form and clear selected section
    setShowRecipeForm(false);
    setSelectedSection(null);
  };

  const hasValidData = data => {
    return data.some(item => Object.values(item).some(value => value !== ""));
  };

  const calculatePieceTotal = pieceData => {
    if (!pieceData) return 0;

    const materialsTotal = pieceData.materials.reduce(
      (sum, mat) => sum + (Number(mat.rate) * Number(mat.avgPerPcs) || 0),
      0,
    );

    const jobsTotal = pieceData.jobs.reduce(
      (sum, job) => sum + (Number(job.rate) || 0),
      0,
    );

    const otherCostsTotal = pieceData.otherCosts.reduce(
      (sum, cost) => sum + (Number(cost.rate) || 0),
      0,
    );

    return materialsTotal + jobsTotal + otherCostsTotal;
  };

  const calculateDesignTotal = () => {
    return Object.values(recipeData).reduce(
      (sum, pieceData) => sum + calculatePieceTotal(pieceData),
      0,
    );
  };

  const handleSaveRecipe = () => {
    setIsViewMode(true);
    // TODO: Implement actual save functionality
    console.log("Recipe Data:", recipeData);
  };

  const handleCancel = () => {
    if (isViewMode) return;

    // If we were editing a section, reset that section's data to original state
    if (selectedSection && recipeData[selectedSection]) {
      setMaterialSets(recipeData[selectedSection].materials);
      setJobSets(recipeData[selectedSection].jobs);
      setOtherCosts(recipeData[selectedSection].otherCosts);
    } else {
      // Reset to initial state if we weren't editing a specific section
      setMaterialSets([
        { materialName: "", unit: "", avgPerPcs: "", rate: "", supplier: "" },
      ]);
      setJobSets([{ jobType: "", rate: "", processDays: "", karigarName: "" }]);
      setOtherCosts([{ costName: "", rate: "" }]);
    }

    setIsViewMode(true);
    setShowRecipeForm(false);
    setSelectedSection(null);
  };

  const handleEdit = () => {
    setIsViewMode(false);
    setShowRecipeForm(false); // Reset to initial state
  };

  const handleSectionEdit = sectionId => {
    setIsViewMode(false);
    setSelectedSection(sectionId);
    setShowRecipeForm(true);

    // Pre-fill the form with existing data
    if (recipeData[sectionId]) {
      setMaterialSets(recipeData[sectionId].materials);
      setJobSets(recipeData[sectionId].jobs);
      setOtherCosts(recipeData[sectionId].otherCosts);
    }
  };

  const handlePrintRecipe = () => {
    // TODO: Implement print functionality
    console.log("Printing recipe");
  };

  const handleDeleteRecipe = () => {
    // TODO: Implement delete functionality
    console.log("Deleting recipe");
  };

  return (
    <div className="bg-white h-[calc(100vh-64px)] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold font-outfit text-gray-800">
              Design Recipe
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isViewMode ? (
            <>
              <button
                onClick={handlePrintRecipe}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-outfit">
                <Printer size={18} />
                Print
              </button>
              <button
                onClick={handleEdit}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-outfit">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
                Edit
              </button>
              <button
                onClick={handleDeleteRecipe}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-outfit">
                <Trash2 size={18} />
                Delete
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-outfit">
                Cancel
              </button>
              <button
                onClick={handleSaveRecipe}
                className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-colors font-outfit">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content Area - Two Vertical Sections */}
      <div className="flex flex-1 min-h-0">
        {/* Left Section - Sections with Action Buttons and Table */}
        <div className="w-1/2 overflow-y-auto border-r">
          <div className="p-6">
            <div className="space-y-2">
              {sections.map(section => (
                <div
                  key={section.id}
                  className="border-b border-gray-300 py-2 px-4">
                  {/* Section Header with Action Buttons */}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold font-outfit text-gray-800">
                      {section.label}
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePrint(section.id)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Print">
                        <Printer size={16} />
                      </button>
                      {recipeData[section.id] &&
                      hasValidData(recipeData[section.id].materials) ? (
                        <button
                          onClick={() => handleSectionEdit(section.id)}
                          className="p-2 text-[#5652B0] hover:bg-[#5652B0]/10 rounded-lg transition-colors flex items-center gap-1"
                          title="Edit">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            <path d="m15 5 4 4" />
                          </svg>
                          Edit
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSectionClick(section.id)}
                          className="p-2 text-[#5652B0] hover:bg-[#5652B0]/10 rounded-lg transition-colors flex items-center gap-1"
                          title="Add">
                          <Plus size={16} />
                          Add
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSectionData(prev => ({
                            ...prev,
                            [section.id]: [],
                          }));
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete All">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Recipe Data Tables */}
                  {recipeData[section.id] && (
                    <div className="mt-4">
                      {recipeData[section.id].materials.length > 0 &&
                        hasValidData(recipeData[section.id].materials) && (
                          <TableContainer className="border border-gray-200 rounded-lg overflow-hidden">
                            <Table size="small">
                              <TableHead>
                                <TableRow className="bg-gray-50">
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Material
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Unit
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Avg/Pcs
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Rate
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Supplier
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {recipeData[section.id].materials.map(
                                  (material, idx) => (
                                    <TableRow
                                      key={idx}
                                      className="hover:bg-gray-50 transition-colors">
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        {material.materialName}
                                      </TableCell>
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        {material.unit}
                                      </TableCell>
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        {material.avgPerPcs}
                                      </TableCell>
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        ₹{material.rate}
                                      </TableCell>
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        {material.supplier}
                                      </TableCell>
                                    </TableRow>
                                  ),
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}

                      {recipeData[section.id].jobs.length > 0 &&
                        hasValidData(recipeData[section.id].jobs) && (
                          <TableContainer className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                            <Table size="small">
                              <TableHead>
                                <TableRow className="bg-gray-50">
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Job Type
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Rate
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Days
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Karigar
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {recipeData[section.id].jobs.map((job, idx) => (
                                  <TableRow
                                    key={idx}
                                    className="hover:bg-gray-50 transition-colors">
                                    <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                      {job.jobType}
                                    </TableCell>
                                    <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                      ₹{job.rate}
                                    </TableCell>
                                    <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                      {job.processDays}
                                    </TableCell>
                                    <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                      {job.karigarName}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}

                      {recipeData[section.id].otherCosts.length > 0 &&
                        hasValidData(recipeData[section.id].otherCosts) && (
                          <TableContainer className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                            <Table size="small">
                              <TableHead>
                                <TableRow className="bg-gray-50">
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Cost Type
                                  </TableCell>
                                  <TableCell className="font-outfit text-sm text-gray-600 font-medium border-b border-gray-200 px-4 py-3">
                                    Rate
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {recipeData[section.id].otherCosts.map(
                                  (cost, idx) => (
                                    <TableRow
                                      key={idx}
                                      className="hover:bg-gray-50 transition-colors">
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        {cost.costName}
                                      </TableCell>
                                      <TableCell className="font-outfit text-sm text-gray-600 px-4 py-3 border-b border-gray-200">
                                        ₹{cost.rate}
                                      </TableCell>
                                    </TableRow>
                                  ),
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="w-1/2 overflow-y-auto">
          <div className="p-6">
            {isViewMode ? (
              <div className="space-y-8 p-4">
                <div>
                  <h2 className="text-xl font-semibold font-outfit text-gray-800 mb-6 text-left">
                    Cost breakup:
                  </h2>
                  <div className="space-y-3 font-outfit">
                    {Object.entries(recipeData)
                      .filter(
                        ([_, data]) =>
                          hasValidData(data.materials) ||
                          hasValidData(data.jobs) ||
                          hasValidData(data.otherCosts),
                      )
                      .map(([sectionId, data]) => {
                        const sectionName = sections.find(
                          s => s.id === sectionId,
                        )?.label;
                        const total = calculatePieceTotal(data);

                        return (
                          <div
                            key={sectionId}
                            className="flex justify-between items-center text-base">
                            <span className="text-gray-700">{sectionName}</span>
                            <div className="flex-1 border-b border-dotted border-gray-300 mx-4"></div>
                            <span className="text-gray-700">
                              ₹ {total.toFixed(2)}
                            </span>
                          </div>
                        );
                      })}

                    <div className="flex justify-between items-center text-base pt-3 border-t border-gray-300">
                      <span className="font-semibold text-gray-800">Total</span>
                      <div className="flex-1 border-b border-dotted border-gray-300 mx-4"></div>
                      <span className="font-semibold text-[#5652B0]">
                        ₹ {calculateDesignTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : showRecipeForm ? (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold font-outfit text-gray-800 mb-4">
                  {recipeData[selectedSection] ? "Edit" : "Add"} Recipe for{" "}
                  {sections.find(s => s.id === selectedSection)?.label}
                </h2>

                {selectedSection !== "otherCosts" && (
                  <>
                    {/* Material Used Section */}
                    <div className="mb-8">
                      <h2 className="text-xl font-semibold font-outfit text-gray-800 text-left mb-4">
                        Material Used
                      </h2>
                      <div className="space-y-3">
                        {materialSets.length === 0 ? (
                          <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                            <button
                              onClick={handleOpenMaterialModal}
                              className="col-span-2 w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5652B0] hover:text-[#5652B0] transition-all font-outfit flex items-center justify-center gap-2">
                              <Plus size={20} />
                              Add Materials
                            </button>
                            <div /> {/* Empty div for grid alignment */}
                          </div>
                        ) : (
                          <>
                            {materialSets.map((materialSet, index) => (
                              <div key={index} className="space-y-3">
                                <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                                  <TextField
                                    label="Material Name"
                                    value={materialSet.materialName}
                                    onClick={handleOpenMaterialModal}
                                    variant="outlined"
                                    size="small"
                                    className="font-outfit cursor-pointer"
                                    InputProps={{
                                      readOnly: true,
                                    }}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Unit"
                                    value={materialSet.unit}
                                    variant="outlined"
                                    size="small"
                                    className="font-outfit"
                                    InputProps={{
                                      readOnly: true,
                                    }}
                                    fullWidth
                                  />
                                  {materialSets.length > 1 && (
                                    <button
                                      onClick={() => removeMaterialSet(index)}
                                      className="text-red-500 hover:bg-red-100 rounded transition-colors h-[40px] w-[40px] flex items-center justify-center"
                                      title="Remove">
                                      <Trash2 size={18} />
                                    </button>
                                  )}
                                </div>
                                <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                                  <TextField
                                    label="Avg per Pcs"
                                    value={materialSet.avgPerPcs}
                                    onChange={e =>
                                      handleMaterialChange(
                                        index,
                                        "avgPerPcs",
                                        e.target.value,
                                      )
                                    }
                                    variant="outlined"
                                    type="number"
                                    size="small"
                                    className="font-outfit"
                                    fullWidth
                                  />
                                  <TextField
                                    label="Rate (₹)"
                                    value={materialSet.rate}
                                    onChange={e =>
                                      handleMaterialChange(
                                        index,
                                        "rate",
                                        e.target.value,
                                      )
                                    }
                                    variant="outlined"
                                    type="number"
                                    size="small"
                                    className="font-outfit"
                                    fullWidth
                                  />
                                  <div /> {/* Empty div for grid alignment */}
                                </div>
                                <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                                  <TextField
                                    select
                                    label="Supplier"
                                    value={materialSet.supplier}
                                    onChange={e =>
                                      handleMaterialChange(
                                        index,
                                        "supplier",
                                        e.target.value,
                                      )
                                    }
                                    variant="outlined"
                                    size="small"
                                    className="font-outfit"
                                    fullWidth>
                                    {suppliers.map(supplier => (
                                      <MenuItem key={supplier} value={supplier}>
                                        {supplier}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <div /> {/* Empty div for grid alignment */}
                                  <div /> {/* Empty div for grid alignment */}
                                </div>
                                {index < materialSets.length - 1 && (
                                  <div className="border-b border-gray-200 my-4"></div>
                                )}
                              </div>
                            ))}
                            <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                              <button
                                onClick={handleOpenMaterialModal}
                                className="col-span-2 w-full mt-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5652B0] hover:text-[#5652B0] transition-all font-outfit flex items-center justify-center gap-2">
                                <Plus size={16} />
                                Add More Materials
                              </button>
                              <div /> {/* Empty div for grid alignment */}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Job Work Section */}
                    <div className="mb-8">
                      <h2 className="text-xl font-semibold font-outfit text-gray-800 text-left mb-4">
                        Job Work
                      </h2>
                      <div className="space-y-3">
                        {jobSets.length === 0 ? (
                          <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                            <button
                              onClick={addJobSet}
                              className="col-span-2 w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5652B0] hover:text-[#5652B0] transition-all font-outfit flex items-center justify-center gap-2">
                              <Plus size={20} />
                              Add Jobs
                            </button>
                            <div /> {/* Empty div for grid alignment */}
                          </div>
                        ) : (
                          <>
                            {jobSets.map((jobSet, index) => (
                              <div key={index} className="space-y-3">
                                <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Job Type</InputLabel>
                                    <Select
                                      value={jobSet.jobType}
                                      onChange={e =>
                                        handleJobChange(
                                          index,
                                          "jobType",
                                          e.target.value,
                                        )
                                      }
                                      label="Job Type">
                                      {jobTypes.map(type => (
                                        <MenuItem key={type} value={type}>
                                          {type}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <TextField
                                    label="Rate (₹)"
                                    value={jobSet.rate}
                                    onChange={e =>
                                      handleJobChange(
                                        index,
                                        "rate",
                                        e.target.value,
                                      )
                                    }
                                    variant="outlined"
                                    type="number"
                                    size="small"
                                    className="font-outfit"
                                    fullWidth
                                  />
                                  {jobSets.length > 1 && (
                                    <button
                                      onClick={() => removeJobSet(index)}
                                      className="text-red-500 hover:bg-red-100 rounded transition-colors h-[40px] w-[40px] flex items-center justify-center"
                                      title="Remove">
                                      <Trash2 size={18} />
                                    </button>
                                  )}
                                </div>
                                <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                                  <TextField
                                    label="Process Days"
                                    value={jobSet.processDays}
                                    onChange={e =>
                                      handleJobChange(
                                        index,
                                        "processDays",
                                        e.target.value,
                                      )
                                    }
                                    variant="outlined"
                                    type="number"
                                    size="small"
                                    className="font-outfit"
                                    fullWidth
                                  />
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Karigar Name</InputLabel>
                                    <Select
                                      value={jobSet.karigarName}
                                      onChange={e =>
                                        handleJobChange(
                                          index,
                                          "karigarName",
                                          e.target.value,
                                        )
                                      }
                                      label="Karigar Name">
                                      {karigars.map(karigar => (
                                        <MenuItem key={karigar} value={karigar}>
                                          {karigar}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <div /> {/* Empty div for grid alignment */}
                                </div>
                                {index < jobSets.length - 1 && (
                                  <div className="border-b border-gray-200 my-4"></div>
                                )}
                              </div>
                            ))}
                            <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                              <button
                                onClick={addJobSet}
                                className="col-span-2 w-full mt-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5652B0] hover:text-[#5652B0] transition-all font-outfit flex items-center justify-center gap-2">
                                <Plus size={16} />
                                Add More Jobs
                              </button>
                              <div /> {/* Empty div for grid alignment */}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Other Cost Section */}
                <div className="mb-8">
                  <h2 className="text-xl font-semibold font-outfit text-gray-800 text-left mb-4">
                    {selectedSection === "otherCosts"
                      ? "Cost Details"
                      : "Other Cost"}
                  </h2>
                  <div className="space-y-3">
                    {otherCosts.length === 0 ? (
                      <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                        <button
                          onClick={addOtherCost}
                          className="col-span-2 w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5652B0] hover:text-[#5652B0] transition-all font-outfit flex items-center justify-center gap-2">
                          <Plus size={20} />
                          Add Other Cost
                        </button>
                        <div /> {/* Empty div for grid alignment */}
                      </div>
                    ) : (
                      <>
                        {otherCosts.map((cost, index) => (
                          <div key={index} className="space-y-3">
                            <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                              <TextField
                                label="Other Cost"
                                value={cost.costName}
                                onChange={e =>
                                  handleOtherCostChange(
                                    index,
                                    "costName",
                                    e.target.value,
                                  )
                                }
                                variant="outlined"
                                size="small"
                                className="font-outfit"
                                fullWidth
                              />
                              <TextField
                                label="Rate (₹)"
                                value={cost.rate}
                                onChange={e =>
                                  handleOtherCostChange(
                                    index,
                                    "rate",
                                    e.target.value,
                                  )
                                }
                                variant="outlined"
                                type="number"
                                size="small"
                                className="font-outfit"
                                fullWidth
                              />
                              {otherCosts.length > 1 && (
                                <button
                                  onClick={() => removeOtherCost(index)}
                                  className="text-red-500 hover:bg-red-100 rounded transition-colors h-[40px] w-[40px] flex items-center justify-center"
                                  title="Remove">
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                            {index < otherCosts.length - 1 && (
                              <div className="border-b border-gray-200 my-4"></div>
                            )}
                          </div>
                        ))}
                        <div className="grid grid-cols-[1fr_1fr_40px] gap-3">
                          <button
                            onClick={addOtherCost}
                            className="col-span-2 w-full mt-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#5652B0] hover:text-[#5652B0] transition-all font-outfit flex items-center justify-center gap-2">
                            <Plus size={16} />
                            Add More Costs
                          </button>
                          <div /> {/* Empty div for grid alignment */}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Add/Update Recipe Button */}
                <button
                  onClick={handleAddToRecipe}
                  className="w-full py-3 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-colors font-outfit flex items-center justify-center gap-2">
                  {recipeData[selectedSection] ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                      Update Recipe
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Add to Recipe
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="p-8 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                  <Package2 size={48} className="text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Select a Piece to Begin
                  </h3>
                  <p className="text-gray-500">
                    Click the add button on any section to start adding recipe
                    details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Material Selection Modal */}
      <Dialog
        open={showMaterialModal}
        onClose={() => setShowMaterialModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "0.5rem",
          },
        }}>
        <div className="border-b border-gray-200">
          <div className="px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Select Materials
            </h2>
            <IconButton
              size="small"
              onClick={() => setShowMaterialModal(false)}>
              <X size={18} />
            </IconButton>
          </div>
        </div>

        <div className="flex flex-col h-[calc(100vh-150px)]">
          <div className="p-6 pb-0">
            <div className="mb-4">
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, color..."
                value={materialSearchQuery}
                onChange={e => setMaterialSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} className="text-gray-400" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-2 pr-2">
              {filteredMaterials.map(material => {
                const isSelected = tempSelectedMaterials.some(
                  m => m.id === material.id,
                );
                return (
                  <div
                    key={material.id}
                    onClick={() => handleMaterialSelect(material)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-purple-50 border-[#5652B0]"
                        : "hover:bg-gray-50 border-gray-200"
                    }`}>
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                      <img
                        src={material.image}
                        alt={material.name}
                        className="w-12 h-12 object-cover rounded-lg bg-gray-100"
                      />
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <div>
                            <h3 className="font-medium text-gray-800">
                              {material.name}
                            </h3>
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <span>
                                {material.colors} • {material.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-800">
                          ₹{material.avgPrice}
                        </div>
                        <div className="text-sm text-gray-500">
                          {material.unit}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowMaterialModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleAddSelectedMaterials}
                disabled={tempSelectedMaterials.length === 0}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:bg-purple-300 flex items-center gap-2">
                {tempSelectedMaterials.length > 0
                  ? `Add ${tempSelectedMaterials.length} Materials`
                  : "Add Materials"}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ViewDesignRecipe;
